import React from "react";
import { motion } from "framer-motion";
import { FileText, ShieldAlert, Database, ChevronRight } from "lucide-react";
import { Link } from "react-router-dom";
import { PageLayout } from "../components/PageLayout";

const SECTIONS = [
  {
    id: "1",
    icon: ShieldAlert,
    title: "1. Uso Aceptable de los Servicios",
    body:
      "Todos los servicios proporcionados por OptiShield X (incluyendo VPS, Servidores Dedicados, Game Servers a través de Pterodactyl y Web Hosting) deben ser utilizados para fines lícitos. Queda estrictamente prohibido alojar, ejecutar o transmitir contenido relacionado con malware, ataques DDoS, botnets, phishing o cualquier software que comprometa la integridad de la red.",
  },
  {
    id: "2",
    icon: Database,
    title: "2. Responsabilidad de Datos y Backups",
    body:
      "Aunque OptiShield X puede ofrecer copias de seguridad automáticas en ciertos planes, el cliente es el único responsable de mantener copias de seguridad de sus archivos, bases de datos (MariaDB, MongoDB, PostgreSQL, etc.) y configuraciones del servidor.",
  },
  {
    id: "3",
    icon: FileText,
    title: "3. Abuso de Recursos",
    body:
      "En los servicios compartidos (Game Servers y Bot Hosting), el uso excesivo de CPU o RAM continuo que afecte al nodo principal puede resultar en la suspensión temporal del servicio hasta que el cliente optimice su consumo o adquiera un plan superior (VPS/Dedicado).",
  },
];

export const TerminosPage = () => {
  return (
    <PageLayout title="Términos y Condiciones | OptiShield X" showBackgroundOrbs>
      <section className="relative osx-noise">
        <div className="absolute inset-0 osx-grid-bg opacity-50" aria-hidden />
        <div className="osx-container relative pt-14 sm:pt-20 pb-10">
          <nav className="flex items-center gap-2 text-xs mb-4" style={{ color: "var(--osx-text-muted)" }}>
            <Link to="/" className="hover:underline" style={{ color: "var(--osx-brand-600)" }}>Inicio</Link>
            <ChevronRight size={12} />
            <span>Legal</span>
            <ChevronRight size={12} />
            <span style={{ color: "var(--osx-text)" }}>Términos</span>
          </nav>
          <motion.div
            initial={{ opacity: 0, y: 14 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-semibold mb-5"
            style={{ background: "var(--osx-surface-2)", border: "1px solid var(--osx-border-strong)", color: "var(--osx-brand-600)" }}
          >
            <FileText size={13} /> Documento legal
          </motion.div>
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.1 }}
            className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight"
            style={{ color: "var(--osx-text)" }}
            data-testid="terminos-headline"
          >
            Términos y <span className="osx-gradient-text">Condiciones</span>
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.25 }}
            className="mt-4 max-w-2xl text-base sm:text-lg"
            style={{ color: "var(--osx-text-muted)" }}
          >
            Conocé las reglas que rigen el uso de nuestros servicios de hosting, VPS, Game Servers y soluciones dedicadas.
          </motion.p>
          <div className="mt-5 inline-flex items-center gap-2 text-[11px] font-mono px-3 py-1.5 rounded-md"
            style={{ background: "var(--osx-surface-2)", border: "1px solid var(--osx-border)", color: "var(--osx-text-muted)" }}>
            <span className="h-1.5 w-1.5 rounded-full" style={{ background: "var(--osx-brand-600)" }} />
            Última actualización: {new Date().toLocaleDateString("es-AR", { year: "numeric", month: "long", day: "numeric" })}
          </div>
        </div>
      </section>

      <section className="relative pb-24">
        <div className="osx-container max-w-4xl space-y-5">
          {SECTIONS.map((s, idx) => (
            <motion.article
              key={s.id}
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.2 }}
              transition={{ duration: 0.5, delay: idx * 0.08 }}
              className="relative p-7 sm:p-9 rounded-2xl osx-gradient-border"
              style={{
                background: "var(--osx-surface)",
                border: "1px solid var(--osx-border)",
                boxShadow: "var(--osx-shadow-1)",
              }}
              data-testid={`terminos-section-${s.id}`}
            >
              <div className="flex items-start gap-4">
                <div
                  className="shrink-0 inline-flex h-12 w-12 items-center justify-center rounded-xl text-white"
                  style={{
                    background: "linear-gradient(135deg, var(--osx-brand-600), var(--osx-accent-cyan))",
                    boxShadow: "0 10px 24px rgba(2,132,199,0.30)",
                  }}
                >
                  <s.icon size={20} strokeWidth={2.2} />
                </div>
                <div className="flex-1 min-w-0">
                  <h2 className="text-xl sm:text-2xl font-bold tracking-tight" style={{ color: "var(--osx-text)" }}>
                    {s.title}
                  </h2>
                  <p className="mt-3 leading-relaxed" style={{ color: "var(--osx-text-muted)" }}>
                    {s.body}
                  </p>
                </div>
              </div>
            </motion.article>
          ))}

          <div
            className="mt-10 p-6 rounded-2xl flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4"
            style={{
              background:
                "linear-gradient(135deg, color-mix(in oklab, var(--osx-brand-600) 12%, var(--osx-surface)), var(--osx-surface))",
              border: "1px solid var(--osx-border-strong)",
            }}
          >
            <div>
              <div className="text-base font-bold" style={{ color: "var(--osx-text)" }}>¿Dudas sobre nuestros términos?</div>
              <div className="text-sm mt-1" style={{ color: "var(--osx-text-muted)" }}>
                Escribinos por Discord o abrí un ticket desde tu panel de facturación.
              </div>
            </div>
            <div className="flex flex-wrap gap-2">
              <Link
                to="/reembolsos"
                className="inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold"
                style={{ background: "var(--osx-surface)", border: "1px solid var(--osx-border-strong)", color: "var(--osx-text)" }}
                data-testid="terminos-reembolsos-link"
              >
                Política de Reembolsos
              </Link>
              <a
                href="https://discord.gg/ExE6pGa4df"
                target="_blank"
                rel="noreferrer noopener"
                className="inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold text-white"
                style={{
                  background: "linear-gradient(135deg, var(--osx-brand-600), var(--osx-accent-cyan))",
                  boxShadow: "0 10px 24px rgba(2,132,199,0.3)",
                }}
                data-testid="terminos-discord-link"
              >
                Discord
              </a>
            </div>
          </div>
        </div>
      </section>
    </PageLayout>
  );
};
